import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Input, InputNumber, Select, AutoComplete, TreeSelect } from 'antd';
import { CsSelect } from 'components/antd-extension/CsSelect';
/**
 * TAPD 字段表单输入组件
 * 根据不同的 html_type 渲染对应的输入控件
 *
 * 支持的 html_type：
 * - input, rich_edit, text: 普通输入框
 * - select, radio: 单选下拉框
 * - multi_select, checkbox: 多选下拉框
 * - user_chooser: 用户选择器（reporter 单选，其他多选）
 * - integer, int: 数字输入框
 * - cascade_checkbox: 级联复选框（TreeSelect，多选，value 为路径字符串数组）
 * - cascade_radio: 级联单选（TreeSelect，单选，value 为路径字符串）
 * - dateinput, datetime: 日期输入（暂用普通输入框）
 * - mix_chooser: 混合选择器（暂用普通输入框）
 * - 其他类型：fallback 到普通输入框
 */
const TapdFieldFormItem = ({ fieldInfo, value, onChange, reporterOptions = [], workspaceUserOptions = [], }) => {
    const { html_type, options, name } = fieldInfo;
    // 防御性编程：如果 fieldInfo 或 html_type 不存在，返回普通输入框
    if (!fieldInfo || !html_type) {
        return (_jsx(Input, { value: value, onChange: (e) => onChange?.(e.target.value), allowClear: true, placeholder: "\u5B57\u6BB5\u4FE1\u606F\u7F3A\u5931" }));
    }
    // 将 options 对象转换为 Select/AutoComplete 可用的选项数组
    const getSelectOptions = () => {
        if (!options || Array.isArray(options)) {
            return [];
        }
        return Object.entries(options).map(([key, val]) => ({
            value: key,
            label: val,
        }));
    };
    // 处理 user_chooser 类型：reporter 单选，其他多选
    if (html_type === 'user_chooser') {
        // reporter 字段：单选，带历史记录，使用 AutoComplete
        // value 和 onChange 直接使用字符串
        if (name === 'reporter') {
            return (_jsx(AutoComplete, { value: value, onChange: onChange, options: reporterOptions, filterOption: (input, option) => option?.value?.toLowerCase()?.includes(input?.toLowerCase()) || false }));
        }
        // 其他 user_chooser 字段（如 current_owner, de, te 等）：多选，值用分号分隔
        // 外部传入的 value 是分号分隔的字符串（如 "user1;user2;user3"）
        // 内部转换为数组给 Select 使用，onChange 时再转换回字符串
        const arrayValue = value ? String(value).split(';').filter(Boolean) : [];
        const handleChange = (selectedValues) => {
            // 将数组转换为分号分隔的字符串传给外部 onChange
            onChange?.(selectedValues.join(';'));
        };
        return (_jsx(Select, { mode: "multiple", value: arrayValue, onChange: handleChange, options: workspaceUserOptions, tokenSeparators: [';'], allowClear: true, showSearch: true, filterOption: (input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase()) || false, dropdownRender: (originNode) => {
                // 仅对 current_owner 字段显示提示信息
                if (name === 'current_owner') {
                    return (_jsxs("div", { children: [originNode, _jsx("div", { style: { padding: '5px 12px', color: '#AAA', borderTop: '1px solid #AAA' }, children: "\u9009\u9879\u53EA\u5217\u51FATAPD\u9879\u76EE\u91CC\u6709\u6743\u9650\u7684\u4EBA\u5458\u3002" })] }));
                }
                return originNode;
            } }));
    }
    // 处理 input 和 rich_edit：普通输入框
    if (html_type === 'input' || html_type === 'rich_edit' || html_type === 'text') {
        return (_jsx(Input, { value: value, onChange: (e) => onChange?.(e.target.value), allowClear: true }));
    }
    // 处理 select 和 radio：单选下拉框
    if (html_type === 'select' || html_type === 'radio') {
        const selectOptions = getSelectOptions();
        return (_jsx(Select, { value: value, onChange: onChange, options: selectOptions, allowClear: true, showSearch: true, optionFilterProp: "label" }));
    }
    // 处理 multi_select 和 checkbox：多选下拉框
    if (html_type === 'multi_select' || html_type === 'checkbox') {
        const selectOptions = getSelectOptions();
        return (_jsx(CsSelect, { ...{ mode: 'multiple' }, value: value, onChange: onChange, options: selectOptions, allowClear: true, showSearch: true, optionFilterProp: "label", style: { width: '100%' } }));
    }
    // 处理 integer 和 int：数字输入框
    if (html_type === 'integer' || html_type === 'int') {
        return (_jsx(InputNumber, { value: value, onChange: onChange, style: { width: '100%' } }));
    }
    // 处理 cascade_checkbox 和 cascade_radio：级联多选/单选，使用 TreeSelect
    // options 是数组格式: [{ name: 'a', children: [{ name: 'b' }] }]
    // 节点 value 使用完整路径（如父节点 'a' 下的子节点 'b' → value 为 'a/b'），以满足 TAPD 的路径格式要求
    // 父节点（有 children）禁止选择，只允许选叶子节点
    if (html_type === 'cascade_checkbox' || html_type === 'cascade_radio') {
        if (Array.isArray(options)) {
            const convertToTreeData = (nodes, parentPath) => {
                return nodes.map((node) => {
                    const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
                    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
                    return {
                        title: node.name,
                        value: nodePath,
                        key: nodePath,
                        disabled: hasChildren,
                        children: hasChildren ? convertToTreeData(node.children, nodePath) : undefined,
                    };
                });
            };
            const treeData = convertToTreeData(options);
            if (html_type === 'cascade_radio') {
                return (_jsx(TreeSelect, { value: value, onChange: onChange, treeData: treeData, treeNodeLabelProp: "value", placeholder: "\u8BF7\u9009\u62E9", allowClear: true, showSearch: true, treeNodeFilterProp: "title", style: { width: '100%' } }));
            }
            // cascade_checkbox：多选，value 是路径字符串数组
            return (_jsx(TreeSelect, { value: value, onChange: onChange, treeData: treeData, treeCheckable: true, treeNodeLabelProp: "value", showCheckedStrategy: TreeSelect.SHOW_CHILD, placeholder: "\u8BF7\u9009\u62E9", allowClear: true, style: { width: '100%' } }));
        }
    }
    // 处理 dateinput 和 datetime：日期输入（暂时用 Input，后续可优化为 DatePicker）
    if (html_type === 'dateinput' || html_type === 'datetime') {
        return (_jsx(Input, { value: value, onChange: (e) => onChange?.(e.target.value), allowClear: true, placeholder: "\u6682\u4E0D\u652F\u6301\u65E5\u671F\u9009\u62E9\u5668\uFF0C\u8BF7\u624B\u52A8\u8F93\u5165" }));
    }
    // 处理 mix_chooser：混合选择器（暂时用 Input，后续可优化）
    if (html_type === 'mix_chooser') {
        return (_jsx(Input, { value: value, onChange: (e) => onChange?.(e.target.value), allowClear: true, placeholder: "\u8BF7\u8F93\u5165" }));
    }
    // 默认 fallback：使用普通输入框
    return (_jsx(Input, { value: value, onChange: (e) => onChange?.(e.target.value), allowClear: true, placeholder: `暂不支持 ${html_type} 类型` }));
};
export default TapdFieldFormItem;
